import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cargosParlamentaresApi,
  type CargoParlamentarApi,
  type TipoCargoParlamentar,
} from '@/lib/api/cargos-parlamentares';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { formatarData } from '@/lib/format';

const TIPOS: Array<{ v: TipoCargoParlamentar; l: string }> = [
  { v: 'comissao', l: 'Comissão' },
  { v: 'mesa_diretora', l: 'Mesa Diretora' },
];

const rotuloTipo = (t: TipoCargoParlamentar) => TIPOS.find((x) => x.v === t)?.l ?? t;

type FormCargo = {
  tipo: TipoCargoParlamentar;
  nome_orgao: string;
  funcao: string;
  data_inicio: string;
  data_fim: string;
};

const FORM_VAZIO: FormCargo = {
  tipo: 'comissao',
  nome_orgao: '',
  funcao: '',
  data_inicio: '',
  data_fim: '',
};

/**
 * Cargos parlamentares do mandato: comissões e Mesa Diretora, com a função
 * exercida e o período. Caso real: a vereadora Professora Claudia
 * (Itaitinga/CE) preside a "Comissão de Educação, Saúde, Direitos Humanos e
 * Assistência Social" desde 11/02/2025, e é 2ª Secretária da Mesa Diretora —
 * os dois cabem aqui, no mesmo cadastro, discriminados por `tipo`.
 */
export function CargosParlamentaresPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<CargoParlamentarApi | null>(null);
  const [form, setForm] = useState<FormCargo>(FORM_VAZIO);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: cargos = [], isLoading } = useQuery({
    queryKey: ['cargos-parlamentares'],
    queryFn: () => cargosParlamentaresApi.list(token),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['cargos-parlamentares'] });

  const criar = useMutation({
    mutationFn: () =>
      cargosParlamentaresApi.create(
        {
          tipo: form.tipo,
          nome_orgao: form.nome_orgao.trim(),
          funcao: form.funcao.trim(),
          data_inicio: form.data_inicio,
          data_fim: form.data_fim || undefined,
        },
        token,
      ),
    onSuccess: () => {
      invalidar();
      toast('Cargo parlamentar cadastrado.');
      fechar();
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar o cargo parlamentar.')),
  });

  const atualizar = useMutation({
    mutationFn: () =>
      cargosParlamentaresApi.update(
        editando!.id,
        {
          tipo: form.tipo,
          nome_orgao: form.nome_orgao.trim(),
          funcao: form.funcao.trim(),
          data_inicio: form.data_inicio,
          data_fim: form.data_fim || null,
        },
        token,
      ),
    onSuccess: () => {
      invalidar();
      toast('Cargo parlamentar atualizado.');
      fechar();
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o cargo parlamentar.')),
  });

  const remover = useMutation({
    mutationFn: (id: string) => cargosParlamentaresApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Cargo parlamentar removido.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover o cargo parlamentar.')),
  });

  const abrirNovo = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setModalAberto(true);
  };

  const abrirEdicao = (c: CargoParlamentarApi) => {
    setEditando(c);
    setForm({
      tipo: c.tipo,
      nome_orgao: c.nome_orgao,
      funcao: c.funcao,
      data_inicio: c.data_inicio.slice(0, 10),
      data_fim: c.data_fim ? c.data_fim.slice(0, 10) : '',
    });
    setModalAberto(true);
  };

  const fechar = () => {
    setModalAberto(false);
    setEditando(null);
    setForm(FORM_VAZIO);
  };

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome_orgao.trim() || !form.funcao.trim() || !form.data_inicio) {
      toast('Preencha o órgão, a função e a data de início.');
      return;
    }
    if (form.data_fim && form.data_fim < form.data_inicio) {
      toast('A data de fim não pode ser anterior à data de início.');
      return;
    }
    if (editando) atualizar.mutate();
    else criar.mutate();
  };

  const excluir = (c: CargoParlamentarApi) => {
    if (!window.confirm(`Remover "${c.funcao}" em "${c.nome_orgao}"?`)) return;
    remover.mutate(c.id);
  };

  return (
    <div className="page" id="p-cargos-parlamentares">
      <div className="head">
        <div>
          <h1>Cargos parlamentares</h1>
          <p>
            Comissões e Mesa Diretora que o mandato ocupa, com a função exercida e o período. Um
            mandato pode acumular mais de um cargo ao mesmo tempo.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={abrirNovo}>
            Novo cargo
          </button>
        </div>
      </div>

      <div className="card flat tw">
        {isLoading ? (
          <p>Carregando...</p>
        ) : cargos.length === 0 ? (
          <p>Nenhum cargo parlamentar cadastrado ainda.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Órgão</th>
                <th>Função</th>
                <th>Início</th>
                <th>Fim</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {cargos.map((c) => (
                <tr key={c.id}>
                  <td>{rotuloTipo(c.tipo)}</td>
                  <td>{c.nome_orgao}</td>
                  <td>{c.funcao}</td>
                  <td>{formatarData(c.data_inicio)}</td>
                  <td>{c.data_fim ? formatarData(c.data_fim) : 'Em curso'}</td>
                  <td>
                    <button className="btn sm" onClick={() => abrirEdicao(c)}>
                      Editar
                    </button>{' '}
                    <button className="btn sm" onClick={() => excluir(c)}>
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        aberto={modalAberto}
        titulo={editando ? 'Editar cargo parlamentar' : 'Novo cargo parlamentar'}
        aoFechar={fechar}
        rodape={
          <>
            <button className="btn" onClick={fechar}>
              Cancelar
            </button>
            <button className="btn pri" onClick={submeter} disabled={criar.isPending || atualizar.isPending}>
              Salvar
            </button>
          </>
        }
      >
        <form className="db" onSubmit={submeter}>
          <label>
            Tipo
            <select
              className="sel"
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoCargoParlamentar })}
            >
              {TIPOS.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.l}
                </option>
              ))}
            </select>
          </label>

          <label>
            Nome do órgão
            <input
              className="in"
              value={form.nome_orgao}
              onChange={(e) => setForm({ ...form, nome_orgao: e.target.value })}
              placeholder="Ex.: Comissão de Educação, Saúde, Direitos Humanos e Assistência Social"
            />
          </label>

          <label>
            Função
            <input
              className="in"
              value={form.funcao}
              onChange={(e) => setForm({ ...form, funcao: e.target.value })}
              placeholder="Ex.: Presidente, 2ª Secretária, Membro"
            />
          </label>

          <label>
            Início
            <input
              className="in"
              type="date"
              value={form.data_inicio}
              onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
            />
          </label>

          <label>
            Fim
            <input
              className="in"
              type="date"
              value={form.data_fim}
              onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
            />
          </label>
        </form>
      </Drawer>
    </div>
  );
}
